import { Button, Option, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { AwardNominationSummary, CountryNominationSummary, NominationRole } from '../../../models';
import { FlexBox } from '../flex-box';
import { SelectOptions } from '../form/SelectOptions';
import './AssignWinnerSection.scss';

interface AssignWinnerSectionProps {
  selectedNominationSummary?: AwardNominationSummary | CountryNominationSummary;
  selectedNominationRole?: NominationRole;
  isCountryAward?: boolean;
  isWinnerAssigned: boolean;
  isFinalistAssigned?: boolean;
  maxWinners: number;
  maxFinalists?: number;
  handleUpdateAwardNominationRole(nominationRole: NominationRole): void;
}

const AssignWinnerSectionImpl: React.FC<AssignWinnerSectionProps> = (props) => {
  const {
    selectedNominationSummary,
    selectedNominationRole,
    handleUpdateAwardNominationRole,
    isCountryAward,
    isFinalistAssigned,
    isWinnerAssigned,
    maxWinners,
    maxFinalists,
  } = props;

  const [role, setRole] = useState('Undesignated');

  const onSaveButtonClicked = () => {
    if (isInputsInvalid() || !selectedNominationSummary) return;

    handleUpdateAwardNominationRole({
      nominationId: selectedNominationSummary.nominationId,
      role: role,
    });
  }

  const isInputsInvalid = (): boolean => {
    return (role === selectedNominationRole?.role) ||
      (role === 'Finalist' && (maxFinalists === undefined || maxFinalists === 0 || !!isFinalistAssigned)) ||
      (role === 'Winner' && (maxWinners === 0 || isWinnerAssigned));
  }

  const resetInputs = () => {
    setRole(selectedNominationRole?.role ?? 'Undesignated');
  }

  const winnerNoun = maxWinners === 1 ? 'winner' : 'winners';
  const finalistNoun = maxFinalists === 1 ? 'finalist' : 'finalists';

  const generateErrorMessage = (): string | undefined => {
    if (role !== 'Finalist' && role !== 'Winner') return undefined;
    if (role === 'Finalist' && (maxFinalists === undefined || maxFinalists === 0)) {
      return 'This award does not allow finalists.';
    }
    if (role === 'Winner' && maxWinners === 0) {
      return 'This award does not allow winners.';
    }
    if (role === 'Finalist' && isFinalistAssigned) {
      return `Only ${maxFinalists} ${finalistNoun} can be designated per award. Please remove designation from another currently assigned finalist before reassigning.`;
    }
    if (role === 'Winner' && isWinnerAssigned) {
      return `Only ${maxWinners} ${winnerNoun} can be designated per award. Please remove designation from a currently assigned winner before reassigning.`;
    }
    return undefined;
  }

  useEffect(resetInputs, [selectedNominationRole]);

  return (
    <FlexBox
      direction='column'
      aligns='start'
      className='assign-winner-section'
    >
      <Text
        as='h3'
        weight='semibold'
        size={500}
        block
      >
        Assign winners
      </Text>
      <Text
        size={200}
        as='p'
        block
      >
        {isCountryAward
          ? `This award can have ${maxWinners} ${winnerNoun} assigned`
          : `This award can have ${maxWinners} ${winnerNoun} and up to ${maxFinalists} ${finalistNoun} assigned.`}
      </Text>
      {
        selectedNominationSummary && (
          <>
            <SelectOptions
              label='Designate as:'
              validationMessage={generateErrorMessage()}
              onOptionSelect={data => setRole(data.optionValue ?? '')}
              dropdownProps={{
                value: role,
                selectedOptions: [role],
              }}
            >
              <Option value='Undesignated'>Undesignated</Option>
              {
                !isCountryAward && <Option value='Finalist'>Finalist</Option>
              }
              <Option value='Winner'>Winner</Option>
            </SelectOptions>
            <Button
              appearance='primary'
              onClick={() => onSaveButtonClicked()}
              disabled={isInputsInvalid()}
            >
              Save
            </Button>
          </>
        )
      }
    </FlexBox>
  );
}

export const AssignWinnerSection = observer(AssignWinnerSectionImpl);
